import { omitBy } from "lodash";
import {safeJSONParse, getLang} from '@/utils';
import  {momentI18nMap } from '@/plugins/i18n';
import {getCountryJson} from "@/apis/payment";
import {isINTaxExempt} from '@/utils/tax';
import {
  checkABNNumber,
  checkAEBNNumber,
  checkIDBNNumber,
  checkKRBNNumber,
  checkMXBNNumber,
  checkSABNNumber,
  checkSGBNNumber,
  checkZABNNumber,
  checkINTaxData,
  checkTINExist,
  checkUINExpireDate
} from '@/utils/tax-number-validation';

export const RESTRICTED_COUNTRIES = ['CU', 'IR', 'KP', 'SY', 'CN'];

export const ADDRESS_ACTION_TYPES = {
  fetchCountryMap: '/get/country/map',
}

export const CONTACT_TYPES = {
  BILLING_TO: 0,
  SOLD_TO: 1
}

export const TAX_ID_STATUS = {
  UNVERIFIED: 'unverified',
  VERIFIED: 'verified',
  FAILED: 'failed',
  STATE_MISMATCH: 'state_mismatch'
}

export const TAX_PREFIX = {
  'GR': 'EL',
  'MC': 'FR',
  'IM': 'GB'
}

export const NO_PREFIX_COUNTRY = ['GR', 'LI', 'MC', 'IM', 'SG', 'AU', 'RU', 'IN'];

// Used for country like 'Monaco'
export const COUNTRY_REPLACEMENT = {
  'MC': 'FR',
  'IM': 'GB',
  'IC': 'ES'
};

export const TAX_TYPE = {
  INPUT: 'input',
  DROPDOWN: 'dropdown'
};

// https://docs.google.com/spreadsheets/d/1dXE8j9GYtNHTAtP4rIPh9mOeDzPXwjwlj33YP0t0yPs/edit?gid=560084279#gid=560084279
// The following changes are not applied to the red country:
// 1. Tooltips copywriting changes
// 2. When the account type is personal, tax form is not displayed.
export const RED_SECTION_REGIONS = [
  'AM',
  'BS',
  'BB',
  'CM',
  'EG',
  'IN',
  'ID',
  'JP',
  'KE',
  'LI',
  'MY',
  'MX',
  'NG',
  'SA',
  'SN',
  'ZA',
  'CH',
  'AE',
  'UZ'
]

export const taxConfigurationGetter = $t => ({
  'PE': {
    message: $t('billing.vat.pe_msg'),
    placeholder: 'RUC',
    checkbox: $t('billing.vat.pe_checkbox'),
    tooltip: $t('billing.vat.pe_tooltip'),
    checkbox_tooltip: $t('billing.vat.pe_tooltip'),
    validation: /^(10|20)\d{9}$/,
    taxNumberTerm: $t('billing.tax_number_term.ruc')
  },
  'AM': {
    message: $t('billing.amMsg'),
    placeholder: $t('billing.vat_registration_placeholder'),
    checkbox: $t('billing.registered_for_vat'),
    tooltip: $t('billing.amMsgDesc'),
    validation: /^\d{8}\/1$/,
    revert: true,
    isEmptiable: true,
    ignorePrefix: true
  },
  'AU': {
    message: $t('billing.not_gst_tips_for_au'),
    prefix: 'ABN',
    taxNumberTerm: $t('billing.tax_number_term.abn'),
    placeholder: $t('billing.tax_number_term.abn'),
    checkbox: $t('billing.not_taxTerm_registered', { taxTerm: $t('billing.tax_term.gst') }),
    checkbox_tooltip: $t('billing.vat.tooltip', { taxTerm: $t('billing.tax_term.gst') }),
    tooltip: $t('billing.gst_checkbox_desc'),
    validation: checkABNNumber,
    ignorePrefix: true
  },
  'BS': {
    message: $t('billing.vat_header_val_BS'),
    placeholder: 'TIN',
    validation: /^\d{9}$/,
    ignorePrefix: true,
    isEmptiable: true,
  },
  'BH': {
    message: $t('billing.vat_header_val_BH'),
    placeholder: $t('billing.vat_id_number_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^\d{15}$/,
    taxDataValidation: checkTINExist,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'BD': {
    message: $t('billing.vat_header_val_BD'),
    placeholder: 'BIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_BD'),
    taxNumberTerm: $t('billing.tax_number_term.bin'),
  },
  'BB': {
    message: $t('billing.vat_header_val_BB'),
    placeholder: $t('billing.vat_number'),
    validation: /^\d{13}$/,
    taxExempt: false,
    isEmptiable: true
  },
  'SN': {
    message: $t('billing.vat_SN_message'),
    placeholder: $t('billing.vat_SN_placeholder'),
    validation: /^[0-9]{9} ?[012][a-zA-Z][0-9]$/,
    isEmptiable: true
  },
  'BY': {
    message: $t('billing.vat.msg_by'),
    placeholder: $t('billing.tin_number'),
    checkbox: $t('billing.vat.checkbox_by'),
    checkbox_tooltip: $t('billing.vat.checkbox_tooltip.by'),
    tooltip: $t('billing.vat.tooltip_by'),
    validation: /^\d{9}$/,
    taxNumberTerm: $t('billing.tax_number_term.tin_full')
  },
  'CO': {
    message: $t('billing.vat.msg_co'),
    placeholder: $t('billing.vat.placeholder_co'),
    checkbox: $t('billing.not_vat_registered'),
    checkbox_tooltip: $t('billing.vat.checkbox_tooltip.co'),
    tooltip: $t('billing.vat.tooltip_co'),
    validation: /^\d{9}-\d$/,
    taxNumberTerm: $t('billing.tax_number_term.rut')
  },
  'KH': {
    message: $t('billing.vat_header_val_KHM'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_KHM'),
    validation: /^[LlKk]00\d-\d{9}$|^[Ee]00\d-\d{10}$/,
    taxNumberTerm: $t('billing.tax_number_term.tin_full')
  },
  'CM': {
    message: $t('billing.vat_header_val_CM'),
    placeholder: 'NIU',
    taxExempt: false,
    isEmptiable: true
  },
  'CL': {
    message: $t('billing.tax_promote_cl'),
    placeholder: $t('billing.rut_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat.tooltip_cl'),
    validation: /^(\w{9})$/,
    ignore:/-|\s|\./g,
    taxNumberTerm: $t('billing.tax_number_term.rut'),
  },
  'EG': {
    message: $t('billing.vat.tax_promote_eg'),
    placeholder: $t('billing.vat.tax_registration_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat.tooltip_eg'),
    validation: /^\d{3}-\d{3}-\d{3}$|^\d{3}\/\d{3}\/\d{3}$|^\d{9}$/,
    uinValidation: /^[A-Za-z0-9]{39}$/,
    taxDataValidation: checkUINExpireDate,
  },
  'GE': {
    message: $t('billing.vat_header_val_GE'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_GE')]),
    validation: /^\d{9}$/,
    taxDataValidation: checkTINExist,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'GH': {
    message: $t('billing.vat_header_val_GH'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^[CV]\d{10}$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'IS': {
    message: $t('billing.vat_header_val_IS'),
    placeholder: $t('billing.vat_id_number_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^\d{5}$|^\d{6}$/,
    taxDataValidation: checkTINExist,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'IN': {
    message: $t('billing.inMsg'),
    placeholder: $t('billing.gst_number'),
    tooltip: $t('billing.gst_checkbox_desc_template', [$t('billing.tax_checkbox_desc_non_inr_in')]),
    validation: /^\d{2}[A-Za-z0-9]{13}$/,
    taxExempt: isINTaxExempt,
    taxDataValidation: checkINTaxData,
    isEmptiable: true,
    revert: true
  },
  'ID': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkIDBNNumber,
    isEmptiable: true
  },
  'JP': {
    message: $t('billing.tax_number_on_invoice_tips'),
    placeholder: $t('billing.tax_number_term.jct_optional'),
    isEmptiable: true
  },
  'KZ': {
    message: $t('billing.vat_header_val_KAZ'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_KAZ')]),
    validation: /^\d{12}$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'KE': {
    message: $t('billing.keMsg'),
    placeholder: $t('billing.pin_number'),
    validation: /^[AP][0-9]{9}[A-Z]$/,
    isEmptiable: true
  },
  'KR': {
    message: $t('billing.vat.msg_kr'),
    prefix: 'KR',
    placeholder: $t('billing.tax_number_term.brn'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: checkKRBNNumber,
    taxNumberTerm: $t('billing.tax_number_term.brn'),
  },
  'LI': {
    message: $t('billing.tax_promote_vat_cat1'),
    prefix: 'MWST',
    placeholder: $t('billing.vat_number'),
    validation: /^\d{5}$/,
    isEmptiable: true
  },
  'MY': {
    message: $t('billing.tax_promote_my'),
    suffix: 'SST',
    placeholder: 'SST Number',
    isEmptiable: true
  },
  'MX': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkMXBNNumber,
    isEmptiable: true
  },
  'MD': {
    message: $t('billing.tax_number_promote.default'),
    placeholder: 'TIN',
    checkbox: $t('billing.tax_checkbox_md'),
    checkbox_tooltip: $t('billing.vat.checkbox_tooltip.md'),
    tooltip: $t('billing.mdMsgDesc'),
    validation: /^1\d{12}$/,
    taxNumberTerm: $t('billing.tax_number_term.tin_full'),
  },
  'NG': {
    message: $t('billing.ngMsg'),
    placeholder: $t('billing.tin_number'),
    validation: /^[0-9]{8}-[0-9]{4}$/,
    isEmptiable: true
  },
  'NO': {
    message: $t('billing.tax_promote_no'),
    placeholder: $t('billing.mva_number'),
    suffix: 'MVA',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.tax_checkbox_desc_template', [$t('billing.tax_checkbox_desc_no')]),
    validation: /^\d{9}$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'OM': {
    message: $t('billing.vat_header_val_OM'),
    placeholder: $t('billing.vat_number'),
    prefix: 'OM',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_OM')]),
    validation: /^\d{10}$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'PH': {
    message: $t('billing.vat.msg_PH'),
    placeholder: 'TIN',
    checkbox: $t('billing.vat.non_business_customer'),
    tooltip: $t('billing.vat.tooltip_PH'),
    checkbox_tooltip: $t('billing.vat.tooltip_PH'),
    validation: /^(?:\d{3}-\d{3}-\d{3}(?:-\d{3})?|\d{9}|\d{12})$/,
    taxNumberTerm: $t('billing.tax_number_term.tin'),
  },
  'RU': {
    message: $t('billing.tax_promote.include_your_vat_number'),
    placeholder: $t('billing.vat_number'),
    prefix: 'RU',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_RU'),
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'SA': {
    message: $t('billing.tax_promote_default'),
    placeholder: $t('billing.vat_number'),
    prefix: 'SA',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: checkSABNNumber
  },
  'RS': {
    message: $t('billing.vat_header_val_RS'),
    placeholder: $t('billing.vat_number'),
    validation: /^\d{9}$/,
    taxExempt: false,
    isEmptiable: true,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'SG': {
    message: $t('billing.not_gst_tips'),
    placeholder: $t('billing.gst_number'),
    prefix: 'GST',
    checkbox: $t('billing.not_gst_registered'),
    checkbox_tooltip: $t('billing.vat.tooltip', { taxTerm: $t('billing.tax_term.gst') }),
    tooltip: $t('billing.gst_checkbox_desc'),
    validation: checkSGBNNumber,
    taxNumberTerm: $t('billing.tax_number_term.gst')
  },
  'ZA': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkZABNNumber,
    isEmptiable: true
  },
  'CH': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: window.checkVATNumber,
    isEmptiable: true
  },
  'TW': {
    message: $t('billing.vat_header_val_TW'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_TW'),
    validation: /^\d{8}$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'TZ': {
    message: $t('billing.vat_header_val_TZ'),
    placeholder: 'VRN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_BD'),
    validation: /^\d{2}-\d{6}-[A-Z]$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'TH': {
    message: $t('billing.vat.msg_th'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: /^\d{13}$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'TR': {
    message: $t('billing.tax_promote_tr'),
    placeholder: $t('billing.tax_input_placeholder'),
    prefix: 'VAT',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.tax_checkbox_desc_template', [$t('billing.tax_checkbox_desc_tr')]),
    validation: /^\d{10,11}$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'UG': {
    message: $t('billing.ugMsg'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_KHM'),
    validation: /^\d{10}$/,
    taxNumberTerm: $t('billing.tax_number_term.tin_full'),
  },
  'UA': {
    message: $t('billing.vat_header_val_UKR'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_UKR')]),
    validation: /^\d{9}$|^\d{10}$|^\d{12}$/,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'UZ': {
    message: $t('billing.tax_promote_tr'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.tax_checkbox_by'),
    tooltip: $t('billing.uzCheckboxMsg'),
    validation: /^\d{12}$/,
    isEmptiable: true,
    revert: true
  },
  'AE': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkAEBNNumber,
    isEmptiable: true
  },
  'VN': {
    message: $t('billing.vat_header_val_VNM'),
    placeholder: $t('billing.tax_input_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_VNM'),
    validation: /^\d{10}$/,
    taxNumberTerm: $t('billing.tax_number_term.tin_full'),
  },
  'GB': {
    message: $t('billing.vat.GB_message'),
    tooltip: $t('billing.vat.UK_tootip'),
    validation: window.checkVATNumber,
    taxNumberTerm: $t('billing.tax_number_term.vat')
  },
  'IC': {
    message: $t('billing.vat.msg_ic'),
    placeholder: 'NIF',
    prefix: 'ES',
    tooltip: $t('billing.vat.tooltip_ic'),
    validation: window.checkVATNumber,
    taxNumberTerm: $t('billing.tax_number_term.nif'),
    checkbox: $t('billing.not_taxTerm_registered', { taxTerm: $t('billing.tax_term.igic') }),
    checkbox_tooltip: $t('billing.vat.tooltip', { taxTerm: $t('billing.tax_term.igic') }),
  }
});

export const EU_DISCLAIM_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL','PT', 'RO', 'SK', 'SI', 'ES', 'SE','GB', 'IS', 'LI', 'NO', 'DE','IM']
export const EU_COUNTRIES = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'SE', 'MC', 'IM', 'GB', 'IC'];
export const UIN_NEEDED_COUNTRIES = ['EG'];

export const ONLINE_VALIDATION_COUNTRIES = ['AU', 'BD', 'BH', 'BY', 'CL', 'CM', 'CO', 'EG', 'GE', 'IN', 'IS', 'KR', 'NO', 'OM', 'PE', 'RU', 'SA', 'SG', 'TH', 'TW', 'UG', 'VN'];

export const getDefaultAddressInfo = () => {
  return {
    address1: '',
    address2: '',
    zip: '',
    city: '',
    state: '',
    country: '',
    countryId: '',
    phone: '',
    accountHq: '',
    companyName: '',
    employeeCount: '',
    usageType: '',
    countryName: '',
    vatNumber: '',
    notVatRegistered: false,
    doingBusinessAsName: '',
    businessType: '',
    otherBusinessType: ''
  }
}

const state = {
  addressInfo: getDefaultAddressInfo(),
  countryMap: {},
  enCountryMap: {},
  ipLocation: safeJSONParse(window.ipLocation || '{}', {}),
  useCaseList: [],
  shippingInfo: null,
  confirmContinueWithErrorPhone: false
}

const getters = {
  addressInfo: (state) => state.addressInfo,
  shippingInfo: (state) => state.shippingInfo,
  confirmContinueWithErrorPhone: (state) => state.confirmContinueWithErrorPhone,
  countryMap: (state) => state.countryMap,
  enCountryMap: (state) => state.enCountryMap,
  useCaseList: (state) => state.useCaseList,
  useNewPhoneValidation: (_, getters) => {
    return !window.campaignFlags?.disablePhoneValidation && getters.getSelectedPlans.some(plan => plan.isVendorProduct);
  },
  countryList: state => {
    const countryMap = state.countryMap;
    const locale = momentI18nMap[getLang] || 'en';
    
    const sortedKeys = Object.keys(countryMap).sort((a, b) => {
      return a.localeCompare(b, locale, { 
        sensitivity: 'base',  
        numeric: true,        
        ignorePunctuation: true
      });
    });
    
    return sortedKeys.reduce((result, label) => {
      result.push({label: label, value: countryMap[label]});
      return result;
    }, [])
  },
}

const mutations = {
  setCountryMap(state, countryMap) {
    state.countryMap = countryMap;
  },
  setShippingInfo(state, data) {
    state.shippingInfo = data;
  },
  setEnCountryMap(state, countryMap) {
    state.enCountryMap = countryMap;
  },
  setAddressInfo(state, data) {
    state.addressInfo = data;
  },
  setUsageType(state,data) {
    state.addressInfo.usageType =  data
  },
  setUseCaseList(state,data) {
    state.useCaseList=  data
  },
  setConfirmContinueWithErrorPhone(state, data) {
    state.confirmContinueWithErrorPhone = data;
  },
}

const actions = {
  async fetchENCountryMap({commit}) {
    try {
      const data = await getCountryJson('en-US');
      commit('setEnCountryMap', data);
    } catch (e) {
      console.error('get country map error');
    }
  },
  async [ADDRESS_ACTION_TYPES.fetchCountryMap]({commit}) {
    try {
      let data = await getCountryJson();
      // data's format is {countryName:countryId}, like {china: cn}
      // need do filter, restrict some forbidden countries
      data = omitBy(data, function (value){
        return RESTRICTED_COUNTRIES.includes(value);
      })
      commit('setCountryMap', data);
    } catch (e) {
      console.error('get country map error');
    }
  },
  updateAddressInfo({commit}, {key, value}) {
    commit('setAddressInfo', {[key]: value});
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}
